/** Formularbausteine und Rechenregeln für Produkte/Varianten.
 *  Bewusst UI-frei gehalten, wo es geht: die Rechenfunktionen sind rein. */
import { eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { NumberInput } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

export const NONE = "__none__";

export const EMPTY_PRODUCT = {
  sku: "", name: "", ean: "", is_digital: false, tax_code: "", cost_price_base: "", surcharges: [],
  price_net: "", price_gross: "", tax_rate: 19, reorder_point: 10, initial_stock: "", initial_lot: "",
  initial_bbd: "", units_per_carton: "", hs_code: "", hts_code_us: "", country_of_origin: "",
  strap_option: false, expiry_tracked: false, price_net_second: "", price_gross_second: "",
  supplier_id: NONE, length_cm: "", width_cm: "", height_cm: "", diameter_cm: "", dimension_uom: "cm",
  weight_unit_value: "", weight_unit_uom: "g", weight_carton_value: "", weight_carton_uom: "kg",
};

const r2 = (n) => Math.round(n * 100) / 100;

/** Maße/Gewichte für das Backend normalisieren (leer -> null). */
export const cleanDims = (obj) => ({
  supplier_id: obj.supplier_id && obj.supplier_id !== NONE ? obj.supplier_id : "",
  length_cm: obj.length_cm === "" ? null : +obj.length_cm,
  width_cm: obj.width_cm === "" ? null : +obj.width_cm,
  height_cm: obj.height_cm === "" ? null : +obj.height_cm,
  diameter_cm: obj.diameter_cm === "" ? null : +obj.diameter_cm,
  dimension_uom: obj.dimension_uom || "cm",
  weight_unit_value: obj.weight_unit_value === "" ? null : +obj.weight_unit_value,
  weight_unit_uom: obj.weight_unit_uom || "g",
  weight_carton_value: obj.weight_carton_value === "" ? null : +obj.weight_carton_value,
  weight_carton_uom: obj.weight_carton_uom || "kg",
});

export const totalCost = (obj) => r2((Number(obj.cost_price_base) || 0)
  + (obj.surcharges || []).reduce((s, x) => s + (Number(x.amount) || 0), 0));

export const dimStr = (v) => {
  const parts = [];
  if (v.diameter_cm != null && v.diameter_cm !== "") parts.push(`⌀${v.diameter_cm}`);
  if (v.length_cm != null && v.length_cm !== "") parts.push(`L${v.length_cm}`);
  if (v.width_cm != null && v.width_cm !== "") parts.push(`B${v.width_cm}`);
  if (v.height_cm != null && v.height_cm !== "") parts.push(`H${v.height_cm}`);
  return parts.length ? `${parts.join(" × ")} ${v.dimension_uom || "cm"}` : "—";
};

/** Netto/Brutto-Kopplung über den Steuersatz — für Standard- und 2.-Wahl-Preise. */
export const recalcPrices = (obj, key, val) => {
  const next = { ...obj, [key]: val };
  const tax = (Number(next.tax_rate ?? 19) || 0) / 100;
  if (key === "price_net" || key === "tax_rate") {
    const n = Number(next.price_net);
    if (next.price_net !== "" && !isNaN(n)) next.price_gross = String(r2(n * (1 + tax)));
  } else if (key === "price_gross") {
    const g = Number(next.price_gross);
    if (next.price_gross !== "" && !isNaN(g)) next.price_net = String(r2(g / (1 + tax)));
  }
  if (key === "price_net_second" || key === "tax_rate") {
    const n2 = Number(next.price_net_second);
    if (next.price_net_second !== "" && next.price_net_second != null && !isNaN(n2)) {
      next.price_gross_second = String(r2(n2 * (1 + tax)));
    } else if (next.price_net_second === "" || next.price_net_second == null) {
      next.price_gross_second = "";
    }
  } else if (key === "price_gross_second") {
    const g2 = Number(next.price_gross_second);
    next.price_net_second = (next.price_gross_second !== "" && next.price_gross_second != null
      && !isNaN(g2)) ? String(r2(g2 / (1 + tax))) : "";
  }
  return next;
};

/** Stück-/Kartongewicht aus der VPE gegenseitig hochrechnen. */
export const recalcWeight = (obj, side) => {
  const vpe = Number(obj.units_per_carton);
  if (!vpe || vpe <= 0) return obj;
  const toG = (val, uom) => Number(val) * (uom === "kg" ? 1000 : 1);
  const r3 = (n) => Math.round(n * 1000) / 1000;
  const next = { ...obj };
  if (side === "unit" && obj.weight_unit_value !== "" && !isNaN(Number(obj.weight_unit_value))) {
    const g = toG(obj.weight_unit_value, obj.weight_unit_uom || "g") * vpe;
    next.weight_carton_value = String(r3((obj.weight_carton_uom || "kg") === "kg" ? g / 1000 : g));
  } else if (side === "carton" && obj.weight_carton_value !== "" && !isNaN(Number(obj.weight_carton_value))) {
    const g = toG(obj.weight_carton_value, obj.weight_carton_uom || "kg") / vpe;
    next.weight_unit_value = String(r3((obj.weight_unit_uom || "g") === "kg" ? g / 1000 : g));
  }
  return next;
};

export const Field = ({ label, name, obj, setObj, type = "text" }) => (
  <div><Label>{label}</Label>{type === "number"
    ? <NumberInput value={obj[name]} onChange={(val) => setObj({ ...obj, [name]: val })} className="mt-1.5" testId={`fld-${name}`} />
    : <Input type={type} value={obj[name]} onChange={(e) => setObj({ ...obj, [name]: e.target.value })} className="mt-1.5" data-testid={`fld-${name}`} />}</div>
);

export const PriceField = ({ label, name, obj, setObj }) => (
  <div><Label>{label}</Label>
    <NumberInput value={obj[name] ?? ""} decimals={2} className="mt-1.5" testId={`fld-${name}`}
      onChange={(val) => setObj(recalcPrices(obj, name, val))} /></div>
);

export const SupplierSelect = ({ obj, setObj, suppliers }) => {
  const { t } = useI18n();
  return (
    <div><Label>{t("common.supplier")}</Label>
      <Select value={obj.supplier_id || NONE} onValueChange={(v) => setObj({ ...obj, supplier_id: v })}>
        <SelectTrigger className="mt-1.5" data-testid="fld-supplier"><SelectValue placeholder={t("common.noSupplier")} /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>{t("common.noSupplier")}</SelectItem>
          {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
        </SelectContent>
      </Select></div>
  );
};

export const TaxSelect = ({ obj, setObj, taxRates }) => {
  const { t } = useI18n();
  return (
    <>
      <div><Label>{t("tax.productCategory")}</Label>
        <Select value={obj.tax_code || "__fixed__"} onValueChange={(v) => setObj({ ...obj, tax_code: v === "__fixed__" ? "" : v })}>
          <SelectTrigger className="mt-1.5" data-testid="fld-tax_code"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__fixed__">{t("tax.fixedRate")}</SelectItem>
            {["STANDARD", "REDUCED", "ZERO_RATE"].map((c) => <SelectItem key={c} value={c}>{t(`tax.code.${c}`, c)}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="mt-1 text-xs text-muted-foreground">{t("tax.categoryHint")}</p>
      </div>
      <div><Label>{t("common.taxRate")}</Label>
        <Select value={String(obj.tax_rate ?? 19)} onValueChange={(v) => setObj(recalcPrices(obj, "tax_rate", Number(v)))}>
          <SelectTrigger className="mt-1.5" data-testid="fld-tax_rate"><SelectValue /></SelectTrigger>
          <SelectContent>{taxRates.map((r) => <SelectItem key={r} value={String(r)}>{r}%{r === 0 ? ` (${t("common.exportRate")})` : ""}</SelectItem>)}</SelectContent>
        </Select></div>
    </>
  );
};

export const DimFields = ({ obj, setObj }) => {
  const { t } = useI18n();
  const dim = (name, placeholder) => (
    <Input type="number" step="0.1" placeholder={placeholder} value={obj[name]}
      onChange={(e) => setObj({ ...obj, [name]: e.target.value })} data-testid={`fld-${name}`} />
  );
  return (
    <div className="col-span-2">
      <div className="flex items-center justify-between">
        <Label>{t("prod.dimHint")}</Label>
        <select className="h-8 rounded-md border border-input bg-background px-2 text-sm"
          value={obj.dimension_uom || "cm"} data-testid="fld-dimension_uom"
          onChange={(e) => setObj({ ...obj, dimension_uom: e.target.value })}>
          <option value="m">{t("prod.uom_m")}</option>
          <option value="dm">{t("prod.uom_dm")}</option>
          <option value="cm">{t("prod.uom_cm")}</option>
        </select>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-1.5">
        {dim("length_cm", t("prod.length"))}{dim("width_cm", t("prod.width"))}
        {dim("height_cm", t("prod.height"))}{dim("diameter_cm", t("prod.diameter"))}
      </div>
    </div>
  );
};

export const WeightFields = ({ obj, setObj }) => {
  const { t } = useI18n();
  const uomSelect = (name, side, def) => (
    <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
      value={obj[name] || def} data-testid={`fld-weight-${side}-uom`}
      onChange={(e) => setObj(recalcWeight({ ...obj, [name]: e.target.value }, side))}>
      <option value="g">g</option><option value="kg">kg</option>
    </select>
  );
  return (
    <div className="col-span-2">
      <Label>{t("prod.weightHint")}</Label>
      <div className="grid grid-cols-2 gap-3 mt-1.5">
        <div className="flex gap-2">
          <Input type="number" step="0.001" placeholder={t("prod.perUnit")} value={obj.weight_unit_value}
            data-testid="fld-weight-unit"
            onChange={(e) => setObj(recalcWeight({ ...obj, weight_unit_value: e.target.value }, "unit"))} />
          {uomSelect("weight_unit_uom", "unit", "g")}
        </div>
        <div className="flex gap-2">
          <Input type="number" step="0.001" placeholder={t("prod.perCarton")} value={obj.weight_carton_value}
            data-testid="fld-weight-carton"
            onChange={(e) => setObj(recalcWeight({ ...obj, weight_carton_value: e.target.value }, "carton"))} />
          {uomSelect("weight_carton_uom", "carton", "kg")}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-1 text-xs text-muted-foreground">
        <span>{t("prod.perUnit")}</span><span>{t("prod.perCarton")}</span>
      </div>
    </div>
  );
};

export const SurchargeEditor = ({ obj, setObj }) => {
  const { t } = useI18n();
  const list = obj.surcharges || [];
  const add = () => setObj({ ...obj, surcharges: [...list, { _k: crypto.randomUUID(), label: "", amount: "" }] });
  const set = (i, patch) => { const arr = [...list]; arr[i] = { ...arr[i], ...patch }; setObj({ ...obj, surcharges: arr }); };
  const remove = (i) => setObj({ ...obj, surcharges: list.filter((_, idx) => idx !== i) });
  return (
    <div className="col-span-2 rounded-md border border-border p-3 space-y-2" data-testid="surcharges-editor">
      <div className="flex items-center justify-between">
        <Label className="text-sm">{t("prod.surcharges")}</Label>
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="add-surcharge-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> {t("prod.surcharges")}
        </Button>
      </div>
      {list.length === 0 && <div className="text-xs text-muted-foreground">{t("prod.noSurcharges")}</div>}
      {list.map((s, i) => (
        <div key={s._k || i} className="flex gap-2 items-center">
          <Input className="flex-1 h-9" placeholder={t("common.name")} value={s.label}
            onChange={(e) => set(i, { label: e.target.value })} data-testid={`surcharge-label-${i}`} />
          <NumberInput className="w-28 h-9" decimals={2} value={s.amount}
            onChange={(val) => set(i, { amount: val })} testId={`surcharge-amount-${i}`} />
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`surcharge-del-${i}`}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ))}
      <div className="flex justify-between border-t border-border pt-2 text-sm">
        <span className="text-muted-foreground">{t("prod.landedCost")}</span>
        <span className="font-mono font-medium text-foreground" data-testid="total-cost-display">{eur(totalCost(obj))}</span>
      </div>
    </div>
  );
};
